import { memo, MutableRefObject, useCallback, useEffect, useState } from 'react';

import { TextVariants } from '@lichtblick/theme';
import { TreeNode } from '@lichtblick/types';

import { ArrowDown, ArrowUp, Folder, FolderContainer, FolderIcon } from './TreeView.styles';

import { Text } from '../Text';

type FolderTreeProps = {
  data: TreeNode;
  folderRefs?: MutableRefObject<Map<number, HTMLDivElement | null>>;
  forceCollapseVersion?: number;
  forceCollapsed?: boolean;
  forceExpandedVersion?: number;
  level?: number;
  onDescendantOpenStateChange?: (folderId: number, isOpen: boolean) => void;
  onFolderSelect: (folder: TreeNode) => void;
  selectedFolder: TreeNode | null;
};

export const findFirstFileFolder = (folder: TreeNode): TreeNode | null => {
  if (folder.type !== 'folder' || !folder.children) {
    return null;
  }

  let deepestFolder: TreeNode | null = null;
  let maxDepth = -1;

  const depthFirstSearch = (currentFolder: TreeNode, currentDepth: number): number => {
    let hasFoundFile = false;

    if (currentFolder && currentFolder.children) {
      for (const child of currentFolder.children) {
        if (child.type === 'file') {
          hasFoundFile = true;
        } else if (child.type === 'folder') {
          const depth = depthFirstSearch(child, currentDepth + 1);

          if (depth > maxDepth) {
            maxDepth = depth;
            deepestFolder = child;
          }
        }
      }
    }

    if (hasFoundFile && currentDepth > maxDepth) {
      maxDepth = currentDepth;
      deepestFolder = currentFolder;
    }

    return maxDepth;
  };

  depthFirstSearch(folder, 0);

  return deepestFolder;
};

const isFolderInPath = (node: TreeNode, selectedFolderId?: number): boolean => {
  if (selectedFolderId == null) {
    return false;
  }

  if (node.id === selectedFolderId) {
    return true;
  }

  if (!node.children) {
    return false;
  }

  return node.children.some((child) => child.type === 'folder' && isFolderInPath(child, selectedFolderId));
};

export const FolderTree = memo(
  ({
    data,
    folderRefs,
    forceCollapseVersion,
    forceCollapsed,
    forceExpandedVersion,
    level = 0,
    onDescendantOpenStateChange,
    onFolderSelect,
    selectedFolder,
  }: FolderTreeProps) => {
    const [isLocalOpen, setIsLocalOpen] = useState(() => !forceCollapsed);
    const [collapseAllVersion, setCollapseAllVersion] = useState(0);
    const [expandAllVersion, setExpandAllVersion] = useState(0);
    const [openDescendantFolderIds, setOpenDescendantFolderIds] = useState<Set<number>>(new Set());

    const isFolderExist = data?.children?.some((child) => child.type === 'folder');

    useEffect(() => {
      if (forceCollapsed) {
        setIsLocalOpen(false);
      }
    }, [forceCollapsed]);

    useEffect(() => {
      if (forceCollapseVersion != null) {
        setIsLocalOpen(false);
      }
    }, [forceCollapseVersion]);

    useEffect(() => {
      if (forceExpandedVersion != null) {
        setIsLocalOpen(true);
      }
    }, [forceExpandedVersion]);

    useEffect(() => {
      if (!forceCollapsed && selectedFolder && selectedFolder.id === data.id) {
        setIsLocalOpen(true);
      }
    }, [data.id, forceCollapsed, selectedFolder]);

    useEffect(() => {
      if (level > 0 && isFolderExist) {
        onDescendantOpenStateChange?.(data.id, isLocalOpen);
      }

      return () => {
        if (level > 0 && isFolderExist) {
          onDescendantOpenStateChange?.(data.id, false);
        }
      };
    }, [data.id, isFolderExist, isLocalOpen, level, onDescendantOpenStateChange]);

    const handleDescendantOpenStateChange = useCallback((folderId: number, isOpen: boolean) => {
      setOpenDescendantFolderIds((currentIds) => {
        const nextIds = new Set(currentIds);

        if (isOpen) {
          nextIds.add(folderId);
        } else {
          nextIds.delete(folderId);
        }

        return nextIds;
      });
    }, []);

    const toggleRootTreeState = useCallback(() => {
      if (openDescendantFolderIds.size > 0) {
        setCollapseAllVersion((value) => value + 1);
        setIsLocalOpen(false);

        return;
      }

      setExpandAllVersion((value) => value + 1);
      setIsLocalOpen(true);
    }, [openDescendantFolderIds.size]);

    const handleSelectFolder = useCallback(() => {
      onFolderSelect(data);
    }, [data, onFolderSelect]);

    const handleToggleFolderByUser = useCallback(() => {
      if (level === 0) {
        toggleRootTreeState();

        return;
      }

      setIsLocalOpen((current) => !current);
    }, [level, toggleRootTreeState]);

    const handleToggleFolder = (e: React.MouseEvent<SVGSVGElement>) => {
      e.stopPropagation();

      handleToggleFolderByUser();
    };

    const countFilesInFolder = (folder: TreeNode): number => {
      if (folder.type !== 'folder' || !folder.children) {
        return 0;
      }

      let count = 0;

      folder.children.forEach((child) => {
        if (child.type === 'file') {
          count++;
        } else if (child.type === 'folder') {
          count += countFilesInFolder(child);
        }
      });

      return count;
    };

    const fileCount = countFilesInFolder(data);
    const isSelected = selectedFolder?.id === data.id;
    const isActivePath = isFolderInPath(data, selectedFolder?.id);
    const shouldRenderChildren = level === 0 || isLocalOpen;
    const childForceCollapsed = level === 0 ? !isLocalOpen : forceCollapsed;
    const childForceCollapseVersion = level === 0 ? collapseAllVersion : forceCollapseVersion;
    const childForceExpandedVersion = level === 0 ? expandAllVersion : forceExpandedVersion;

    return (
      <div
        key={data.id}
        ref={(el) => {
          if (el) {
            folderRefs?.current.set(data.id, el);
          }
        }}
      >
        <Folder
          $isActivePath={isActivePath}
          $isSelected={isSelected}
          $level={level}
          onClick={handleSelectFolder}
          onDoubleClick={handleToggleFolderByUser}
        >
          <FolderContainer>
            {!isFolderExist && <FolderIcon />}
            <Text $variant={TextVariants.body.m}>{data.name}</Text>
            {fileCount && fileCount > 0 && <Text>{`(${fileCount})`}</Text>}
          </FolderContainer>
          {isFolderExist &&
            (isLocalOpen ? <ArrowDown onClick={handleToggleFolder} /> : <ArrowUp onClick={handleToggleFolder} />)}
        </Folder>
        {shouldRenderChildren &&
          data?.children
            ?.filter((child) => child.type === 'folder')
            .map((child) => (
              <div
                key={child.id}
                ref={(el) => {
                  if (el) {
                    folderRefs?.current.set(child.id, el);
                  }
                }}
              >
                <FolderTree
                  data={child}
                  folderRefs={folderRefs}
                  forceCollapseVersion={childForceCollapseVersion}
                  forceCollapsed={childForceCollapsed}
                  forceExpandedVersion={childForceExpandedVersion}
                  key={child.id}
                  level={level + 1}
                  onDescendantOpenStateChange={
                    level === 0 ? handleDescendantOpenStateChange : onDescendantOpenStateChange
                  }
                  onFolderSelect={onFolderSelect}
                  selectedFolder={selectedFolder}
                />
              </div>
            ))}
      </div>
    );
  },
);
